import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/_core/hooks/useAuth";
import { useLocation } from "wouter";
import { AlertCircle, Wand2, CheckCircle, BookOpen, Edit2, Save, X, AlertTriangle, Upload, Loader2, ArrowLeft } from "lucide-react";
import { useState, useCallback } from "react";
import ERGOSWizard from "@/components/ERGOSWizard";
import { trpc } from "@/lib/trpc";
import SalviaLayout from "@/components/SalviaLayout";
import ValidationResultCard from "@/components/ValidationResultCard";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { QualificationMatrixTab } from "@/components/QualificationMatrixTab";
import { SourcesMatrixTab } from "@/components/SourcesMatrixTab";
import { ClassificationMatrixTab } from "@/components/ClassificationMatrixTab";
import { SuggestionsMatrixTab } from "@/components/SuggestionsMatrixTab";
import { usePageTitle } from "@/hooks/usePageTitle";

// Feature flag do servidor
const ENV = { ergosEditEnabled: true }; // Default true para dev, será sobrescrito pelo servidor

// Componente da Aba Template com Edição Direta
function TemplateTabContent({ setActiveTab }: { setActiveTab: (tab: string) => void }) {
  const { user } = useAuth();
  
  // Track which templateId to display (undefined = PUBLISHED oficial)
  const [viewingTemplateId, setViewingTemplateId] = useState<number | undefined>(undefined);
  
  const { data: template, isLoading, refetch } = trpc.ergos.getFullTemplate.useQuery(
    viewingTemplateId ? { templateId: viewingTemplateId } : undefined
  );
  const saveQuestionnaireVersionMutation = trpc.ergos.saveQuestionnaireVersion.useMutation();
  const publishMutation = trpc.ergos.publishTemplate.useMutation();
  const cloneMutation = trpc.ergos.cloneTemplateForEdit.useMutation();
  const utils = trpc.useUtils();
  
  // Verificar se usuário é administrador interno com alcance operacional completo no NR1
  const isAdminMaster = user?.role === "admin" || user?.role === "salvia_operation_admin";
  const canEdit = ENV.ergosEditEnabled && isAdminMaster;
  
  const [isEditing, setIsEditing] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [editVersion, setEditVersion] = useState<string | null>(null);
  const [editedData, setEditedData] = useState<any>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showValidation, setShowValidation] = useState(false);

  // Garantir que sempre exibe o template correto
  const displayData = isEditing && editedData ? editedData : (template || null);

  const handleEditClick = async () => {
    if (!template) return;
    
    // Se a versão está PUBLISHED, criar clone para edição
    if (template.status === "PUBLISHED") {
      try {
        const toastId = toast.loading("Criando nova versão para edição...");
        const result = await cloneMutation.mutateAsync({ templateId: template.id });
        toast.dismiss(toastId);
        
        if (result.isClone) {
          toast.success(`Nova versão ${result.newVersion} criada para edição`);
        }
        
        // Apontar para o DRAFT recém-criado e recarregar
        setViewingTemplateId(result.newTemplateId);
        await utils.ergos.getFullTemplate.invalidate();
        // Aguardar refetch com o novo templateId
        const freshData = await utils.ergos.getFullTemplate.fetch(
          { templateId: result.newTemplateId }
        );
        
        setIsEditing(true);
        setEditedData(JSON.parse(JSON.stringify(freshData)));
        setEditVersion(result.newVersion);
        setValidationErrors([]);
      } catch (error: any) {
        toast.error("Erro ao criar versão para edição", {
          description: error.message || "Ocorreu um erro inesperado.",
        });
      }
    } else {
      // Template já é DRAFT — editar diretamente
      setViewingTemplateId(template.id);
      setIsEditing(true);
      setEditedData(JSON.parse(JSON.stringify(template)));
      // Remover "v" da versão antes de fazer parseFloat
      const versionString = template?.version?.replace(/^v/, "") || "1.0";
      const currentVersion = parseFloat(versionString);
      const nextVersion = currentVersion + 0.1;
      const newVersion = nextVersion.toFixed(1);
      console.log("[ERGOS] Calculando versão:", { currentVersion, nextVersion, newVersion });
      setEditVersion(newVersion); // Salvar sem o "v" prefix
      setValidationErrors([]);
    }
  };

  const handlePublish = async () => {
    if (!template?.id) return;
    
    setIsPublishing(true);
    try {
      const result = await publishMutation.mutateAsync({ templateId: template.id });
      toast.success(result.message || "Template publicado com sucesso!", {
        description: `Versão ${result.version} agora está ativa.`,
      });
      // Após publicar, voltar a visualizar o PUBLISHED oficial
      setViewingTemplateId(undefined);
      await utils.ergos.getFullTemplate.invalidate();
    } catch (error: any) {
      const errorMsg = error.message || "Ocorreu um erro ao publicar.";
      if (errorMsg.includes("VALIDATION_ERROR")) {
        toast.error("Validação falhou", {
          description: errorMsg.replace("VALIDATION_ERROR: ", ""),
        });
      } else {
        toast.error("Erro ao publicar", {
          description: errorMsg,
        });
      }
    } finally {
      setIsPublishing(false);
    }
  };

  const handleCancel = async () => {
    setIsEditing(false);
    setEditedData(null);
    setEditVersion(null);
    setValidationErrors([]);
    // Voltar a visualizar o PUBLISHED oficial
    setViewingTemplateId(undefined);
    await utils.ergos.getFullTemplate.invalidate();
  };

  const handleSave = async () => {
    const errors: string[] = [];
    
    if (!editedData?.modules || editedData.modules.length === 0) {
      errors.push("Nenhum módulo encontrado");
    }

    editedData?.modules?.forEach((module: any, idx: number) => {
      if (!module.name || module.name.trim() === "") {
        errors.push(`Módulo ${idx + 1}: Nome vazio`);
      }
      module.questions?.forEach((q: any, qIdx: number) => {
        if (!q.text || q.text.trim() === "") {
          errors.push(`Módulo ${idx + 1}, Pergunta ${qIdx + 1}: Texto vazio`);
        }
        q.answers?.forEach((a: any, aIdx: number) => {
          if (!a.label || a.label.trim() === "") {
            errors.push(`Módulo ${idx + 1}, Pergunta ${qIdx + 1}, Resposta ${aIdx + 1}: Label vazio`);
          }
          if (!Number.isInteger(a.score) || a.score < 0 || a.score > 10) {
            errors.push(`Módulo ${idx + 1}, Pergunta ${qIdx + 1}, Resposta ${aIdx + 1}: Peso deve ser um número inteiro entre 0 e 10`);
          }
        });
      });
    });

    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    try {
      const toastId = toast.loading("Salvando nova versão...");
      
      // Garantir que a versão é numérica
      const finalVersion = editVersion || "1.0";
      console.log("[ERGOS] Salvando versão:", { editVersion, finalVersion });
      
      const response = await saveQuestionnaireVersionMutation.mutateAsync({
        version: finalVersion,
        globalQuestions: editedData.globalQuestions || [],
        modules: editedData.modules,
      });
      
      if (!response?.success || !response?.id) {
        throw new Error("Resposta inválida do servidor");
      }
      
      toast.dismiss(toastId);
      toast.success(`Nova versão ${response?.version || editVersion || "1.0"} do Questionário ERGOS salva como RASCUNHO.`);
      
      // Apontar para o DRAFT recém-salvo e recarregar
      setViewingTemplateId(response.id);
      await utils.ergos.getFullTemplate.invalidate();
      
      setIsEditing(false);
      setEditedData(null);
      setEditVersion(null);
      setValidationErrors([]);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : "Erro desconhecido";
      toast.error(`Não foi possível salvar. ${errorMsg}`);
      // Manter modo edição em caso de erro
      setIsEditing(true);
    }
  };

  const handleModuleNameChange = (moduleIdx: number, newName: string) => {
    if (!editedData) return;
    const updated = JSON.parse(JSON.stringify(editedData));
    updated.modules[moduleIdx].name = newName;
    setEditedData(updated);
  };

  const handleQuestionTextChange = (moduleIdx: number, qIdx: number, newText: string) => {
    if (!editedData) return;
    const updated = JSON.parse(JSON.stringify(editedData));
    updated.modules[moduleIdx].questions[qIdx].text = newText;
    setEditedData(updated);
  };

  const handleAnswerLabelChange = (moduleIdx: number, qIdx: number, aIdx: number, newLabel: string) => {
    if (!editedData) return;
    const updated = JSON.parse(JSON.stringify(editedData));
    updated.modules[moduleIdx].questions[qIdx].answers[aIdx].label = newLabel;
    setEditedData(updated);
  };

  const handleAnswerScoreChange = (moduleIdx: number, qIdx: number, aIdx: number, newScore: number) => {
    if (!editedData) return;
    const updated = JSON.parse(JSON.stringify(editedData));
    updated.modules[moduleIdx].questions[qIdx].answers[aIdx].score = newScore;
    setEditedData(updated);
  };

  // Handlers para globalQuestions (perguntas informativas)
  const handleGlobalQuestionTextChange = (qIdx: number, newText: string) => {
    if (!editedData) return;
    const updated = JSON.parse(JSON.stringify(editedData));
    if (!updated.globalQuestions) updated.globalQuestions = [];
    updated.globalQuestions[qIdx].text = newText;
    setEditedData(updated);
  };

  const handleGlobalQuestionRequiredChange = (qIdx: number, isRequired: boolean) => {
    if (!editedData) return;
    const updated = JSON.parse(JSON.stringify(editedData));
    if (!updated.globalQuestions) updated.globalQuestions = [];
    updated.globalQuestions[qIdx].isRequired = isRequired;
    setEditedData(updated);
  };

  const handleGlobalQuestionMinLengthChange = (qIdx: number, minLength: number) => {
    if (!editedData) return;
    const updated = JSON.parse(JSON.stringify(editedData));
    if (!updated.globalQuestions) updated.globalQuestions = [];
    updated.globalQuestions[qIdx].minLength = minLength;
    setEditedData(updated);
  };

  const handleGlobalQuestionMaxLengthChange = (qIdx: number, maxLength: number) => {
    if (!editedData) return;
    const updated = JSON.parse(JSON.stringify(editedData));
    if (!updated.globalQuestions) updated.globalQuestions = [];
    updated.globalQuestions[qIdx].maxLength = maxLength;
    setEditedData(updated);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Template do Questionário</CardTitle>
          <CardDescription>Definir estrutura e campos do questionário ERGOS</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-12 bg-gray-200 rounded animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!displayData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Template do Questionário</CardTitle>
          <CardDescription>Definir estrutura e campos do questionário ERGOS</CardDescription>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Nenhum template encontrado. Inicialize um novo template para começar.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <BookOpen size={20} />
              Template do Questionário
            </CardTitle>
            <CardDescription>
              {isEditing ? (
                <span className="text-amber-600 font-medium">
                  ✎ Você está editando uma nova versão do Questionário ERGOS
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Versão {displayData?.version || "1.0.0"} - 
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                    displayData?.status === "PUBLISHED" 
                      ? "bg-green-100 text-green-800" 
                      : "bg-amber-100 text-amber-800"
                  }`}>
                    {displayData?.status === "PUBLISHED" && <CheckCircle size={12} />}
                    {displayData?.status || "DRAFT"}
                  </span>
                </span>
              )}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            {!isEditing ? (
              <>
                <Button 
                  onClick={handleEditClick} 
                  className="gap-2"
                  disabled={!canEdit || cloneMutation.isPending}
                  title={
                    !ENV.ergosEditEnabled ? "Edição desabilitada por feature flag" :
                    !isAdminMaster ? "Apenas Admin Master pode editar" :
                    ""
                  }
                >
                  {cloneMutation.isPending ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Edit2 size={16} />
                  )}
                  {displayData?.status === "PUBLISHED" ? "Editar (criar nova versão)" : "Editar Template"}
                </Button>
                {displayData?.status === "DRAFT" && (
                  <Button 
                    onClick={handlePublish} 
                    className="gap-2 bg-green-600 hover:bg-green-700"
                    disabled={isPublishing || !canEdit}
                  >
                    {isPublishing ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Upload size={16} />
                    )}
                    Publicar
                  </Button>
                )}
                {viewingTemplateId && (
                  <Button 
                    onClick={() => {
                      setViewingTemplateId(undefined);
                      utils.ergos.getFullTemplate.invalidate();
                    }} 
                    variant="outline" 
                    className="gap-2"
                  >
                    <ArrowLeft size={16} />
                    Voltar ao Oficial
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button onClick={handleSave} className="gap-2 bg-green-600 hover:bg-green-700">
                  <Save size={16} />
                  Salvar
                </Button>
                <Button onClick={handleCancel} variant="outline" className="gap-2">
                  <X size={16} />
                  Cancelar
                </Button>
              </>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {validationErrors.length > 0 && (
          <Alert className="border-red-200 bg-red-50">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              <div className="font-semibold mb-2">Erros encontrados:</div>
              <ul className="list-disc list-inside space-y-1">
                {validationErrors.map((err, idx) => (
                  <li key={idx} className="text-sm">{err}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {!isEditing ? (
          // Modo READ-ONLY
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-gray-600">Total de Módulos</p>
                <p className="text-2xl font-bold text-blue-600">{displayData.modules.length}</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-gray-600">Grupo A</p>
                <p className="text-2xl font-bold text-green-600">
                  {displayData.modules.filter((m: any) => m.group === "A").length}
                </p>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <p className="text-sm text-gray-600">Grupo B</p>
                <p className="text-2xl font-bold text-purple-600">
                  {displayData.modules.filter((m: any) => m.group === "B").length}
                </p>
              </div>
            </div>

            {/* Perguntas Iniciais (Informativas) */}
            {displayData.globalQuestions && displayData.globalQuestions.length > 0 && (
              <div className="space-y-4 mb-6">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  Perguntas Iniciais (Informativas)
                  <span className="text-xs font-normal text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    {displayData.globalQuestions.length} pergunta{displayData.globalQuestions.length !== 1 ? 's' : ''}
                  </span>
                </h3>
                <div className="grid gap-3">
                  {displayData.globalQuestions.map((question: any, idx: number) => (
                    <div
                      key={question.code}
                      className="border border-blue-200 rounded-lg p-4 bg-blue-50/50"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="px-2 py-1 rounded text-xs font-semibold bg-blue-100 text-blue-800">
                              Informativa (sem pontuação)
                            </span>
                            <span className="text-xs text-gray-500">#{idx + 1}</span>
                            <span className="text-xs text-gray-500">• {question.answerType}</span>
                          </div>
                          <p className="font-medium text-gray-900">{question.text}</p>
                          <div className="mt-2 text-xs text-gray-600">
                            {question.isRequired && (
                              <span className="inline-flex items-center gap-1 mr-3">
                                <span className="text-red-500">*</span> Obrigatória
                              </span>
                            )}
                            {question.minLength && (
                              <span className="mr-3">Mín: {question.minLength} caracteres</span>
                            )}
                            {question.maxLength && (
                              <span>Máx: {question.maxLength} caracteres</span>
                            )}
                          </div>
                        </div>
                        <CheckCircle className="text-blue-500" size={20} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Módulos Configurados</h3>
              <div className="grid gap-3">
                {displayData.modules.map((module: any, idx: number) => (
                  <div
                    key={module.code}
                    className="border rounded-lg p-4 hover:bg-gray-50 transition"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            module.group === "A"
                              ? "bg-green-100 text-green-800"
                              : "bg-purple-100 text-purple-800"
                          }`}>
                            Grupo {module.group}
                          </span>
                          <span className="text-xs text-gray-500">#{idx + 1}</span>
                        </div>
                        <h4 className="font-semibold text-gray-900">{module.name}</h4>
                        <p className="text-sm text-gray-600 mt-1">
                          {module.questions.length} pergunta{module.questions.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <CheckCircle className="text-green-500" size={20} />
                    </div>

                    <div className="mt-3 pl-4 border-l-2 border-gray-200 space-y-2">
                      {module.questions.map((q: any, qIdx: number) => (
                        <div key={q.code} className="text-sm">
                          <p className="font-medium text-gray-700">{q.text}</p>
                          {q.description && (
                            <p className="text-xs text-gray-500 mt-0.5 italic">{q.description}</p>
                          )}
                          <div className="flex gap-2 mt-1">
                            {q.answers.map((a: any) => (
                              <span
                                key={`${q.code}-${a.order}`}
                                className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                              >
                                {a.label} ({a.score})
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-amber-900 mb-2">Fórmula Final</p>
              <p className="text-sm text-amber-800 font-mono">
                Risco ERGOS = 0,83 × (Grupo A + Grupo B)
              </p>
              <p className="text-xs text-amber-700 mt-2">
                Cada grupo é calculado como a soma dos scores das 5 perguntas (0-20).
              </p>
            </div>

            {/* Info sobre publicação */}
            {displayData?.status === "DRAFT" && (
              <div className="flex items-center gap-2 pt-4 border-t text-sm text-muted-foreground">
                <AlertTriangle size={16} className="text-amber-500" />
                <span>Este template está em rascunho. Clique em "Publicar" para torná-lo oficial.</span>
              </div>
            )}
            {displayData?.status === "PUBLISHED" && (
              <div className="flex items-center gap-2 pt-4 border-t text-sm text-green-700">
                <CheckCircle size={16} />
                <span>Este template está publicado e ativo. Para editar, uma nova versão será criada.</span>
              </div>
            )}
          </div>
        ) : (
          // Modo EDIÇÃO
          <div className="space-y-4">
            {/* Perguntas Iniciais (Informativas) - Editável */}
            {displayData.globalQuestions && displayData.globalQuestions.length > 0 && (
              <div className="space-y-4 mb-6">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  Perguntas Iniciais (Informativas)
                  <span className="text-xs font-normal text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    {displayData.globalQuestions.length} pergunta{displayData.globalQuestions.length !== 1 ? 's' : ''}
                  </span>
                </h3>
                <div className="grid gap-3">
                  {displayData.globalQuestions.map((question: any, qIdx: number) => {
                    // Validações inline
                    const minLength = question.minLength || 0;
                    const maxLength = question.maxLength || 5000;
                    const hasMinError = minLength < 0;
                    const hasMaxError = maxLength > 5000 || maxLength < minLength;
                    const hasError = hasMinError || hasMaxError;

                    return (
                      <div
                        key={question.code}
                        className="border border-blue-200 rounded-lg p-4 bg-blue-50/30 space-y-3"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-1 rounded text-xs font-semibold bg-blue-100 text-blue-800">
                            Informativa (sem pontuação)
                          </span>
                          <span className="text-xs text-gray-500">#{qIdx + 1}</span>
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                            {question.answerType} (travado)
                          </span>
                        </div>

                        {/* Texto da pergunta */}
                        <div className="space-y-1">
                          <label className="text-xs font-medium text-gray-700">Texto da Pergunta</label>
                          <Input
                            value={question.text}
                            onChange={(e) => handleGlobalQuestionTextChange(qIdx, e.target.value)}
                            className="font-medium"
                            placeholder="Digite o texto da pergunta"
                          />
                        </div>

                        {/* Obrigatória */}
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`required-${qIdx}`}
                            checked={question.isRequired}
                            onChange={(e) => handleGlobalQuestionRequiredChange(qIdx, e.target.checked)}
                            className="w-4 h-4 rounded border-gray-300"
                          />
                          <label htmlFor={`required-${qIdx}`} className="text-sm text-gray-700">
                            Pergunta obrigatória
                          </label>
                        </div>

                        {/* Min/Max Length */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <label className="text-xs font-medium text-gray-700">Mínimo de caracteres</label>
                            <Input
                              type="number"
                              value={question.minLength || 0}
                              onChange={(e) => handleGlobalQuestionMinLengthChange(qIdx, parseInt(e.target.value) || 0)}
                              className={`text-sm ${hasMinError ? 'border-red-500' : ''}`}
                              placeholder="0"
                              min="0"
                            />
                            {hasMinError && (
                              <p className="text-xs text-red-600">Mínimo deve ser ≥ 0</p>
                            )}
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs font-medium text-gray-700">Máximo de caracteres</label>
                            <Input
                              type="number"
                              value={question.maxLength || 5000}
                              onChange={(e) => handleGlobalQuestionMaxLengthChange(qIdx, parseInt(e.target.value) || 5000)}
                              className={`text-sm ${hasMaxError ? 'border-red-500' : ''}`}
                              placeholder="5000"
                              max="5000"
                            />
                            {hasMaxError && (
                              <p className="text-xs text-red-600">
                                {maxLength > 5000 ? 'Máximo deve ser ≤ 5000' : 'Máximo deve ser ≥ mínimo'}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Aviso */}
                        <div className="bg-blue-100 border border-blue-200 rounded p-2 text-xs text-blue-800">
                          🛈 Esta pergunta <strong>não afeta o cálculo</strong> de risco ERGOS e serve apenas para coletar informações qualitativas.
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Módulos Configurados */}
            {displayData.modules.map((module: any, moduleIdx: number) => (
              <div key={module.code} className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center gap-3">
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                    module.group === "A"
                      ? "bg-green-100 text-green-800"
                      : "bg-purple-100 text-purple-800"
                  }`}>
                    Grupo {module.group}
                  </span>
                  <Input
                    value={module.name}
                    onChange={(e) => handleModuleNameChange(moduleIdx, e.target.value)}
                    className="flex-1 font-semibold"
                    placeholder="Nome do módulo"
                  />
                </div>

                <div className="space-y-3 pl-4 border-l-2 border-gray-200">
                  {module.questions.map((question: any, qIdx: number) => (
                    <div key={question.code} className="space-y-2">
                      <Input
                        value={question.text}
                        onChange={(e) => handleQuestionTextChange(moduleIdx, qIdx, e.target.value)}
                        className="text-sm font-medium"
                        placeholder="Texto da pergunta"
                      />

                      {/* Descrição da pergunta (editável) */}
                      <div className="space-y-1">
                        <label className="text-xs font-medium text-gray-500">Descrição da pergunta (opcional)</label>
                        <textarea
                          value={question.description || ''}
                          onChange={(e) => {
                            const newModules = [...displayData.modules];
                            newModules[moduleIdx].questions[qIdx].description = e.target.value;
                            setEditedData({ ...displayData, modules: newModules });
                          }}
                          className="w-full text-xs text-gray-600 border border-gray-200 rounded-md p-2 resize-y min-h-[40px] max-h-[120px] focus:outline-none focus:ring-1 focus:ring-blue-400"
                          placeholder="Descreva o contexto ou orientação para esta pergunta..."
                          maxLength={1000}
                        />
                        <div className="text-right text-xs text-gray-400">
                          {(question.description || '').length}/1000
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {question.answers.map((answer: any, aIdx: number) => (
                          <div key={`${question.code}-${aIdx}`} className="flex items-center gap-1">
                            <Input
                              value={answer.label}
                              onChange={(e) => handleAnswerLabelChange(moduleIdx, qIdx, aIdx, e.target.value)}
                              className="text-xs w-32"
                              placeholder="Label"
                            />
                            <Input
                              type="number"
                              value={answer.score}
                              onChange={(e) => handleAnswerScoreChange(moduleIdx, qIdx, aIdx, parseInt(e.target.value))}
                              className="text-xs w-16"
                              placeholder="Peso"
                              min="0"
                              max="4"
                              step="2"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function ERGOS() {
  usePageTitle("Motor ERGOS");
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const isInternalAdmin = user?.role === "admin" || user?.role === "salvia_operation_admin";
  const [wizardOpen, setWizardOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("template");
  const utils = trpc.useUtils();
  
  // Prefetch da Matriz de Classificação ao passar mouse
  const handleTabMouseEnter = useCallback((tabId: string) => {
    if (tabId === "classificacao") {
      // Prefetch da query getCurrentRiskMatrix
      utils.ergos.classificationMatrix.getCurrentRiskMatrix.prefetch({});
      // Prefetch da query getPublishedQualifiers
      utils.ergos.classificationMatrix.getPublishedQualifiers.prefetch({});
    }
  }, [utils]);

  if (!isInternalAdmin) {
    return (
      <SalviaLayout title="ERGOS">
        <div className="flex items-center justify-center h-full">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="text-red-500" size={24} />
                Acesso Não Autorizado
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                O Submódulo ERGOS é exclusivo para administradores internos da Sálvia.
              </p>
              <Button 
                onClick={() => setLocation("/motor-analise")}
                className="mt-4 w-full"
              >
                Voltar ao Motor de Análise
              </Button>
            </CardContent>
          </Card>
        </div>
      </SalviaLayout>
    );
  }

  const abas = [
    {
      id: "template",
      label: "Template do Questionário",
      descricao: "Definir estrutura e campos do questionário ERGOS",
      prompt: 3,
    },
    {
      id: "qualificacao",
      label: "Matriz de Qualificação",
      descricao: "Configurar matriz de qualificação de riscos",
      prompt: 4,
    },
    {
      id: "fontes",
      label: "Matriz de Fontes Geradoras",
      descricao: "Definir fontes geradoras de riscos psicossociais",
      prompt: 5,
    },
    {
      id: "classificacao",
      label: "Matriz de Classificação (P × S)",
      descricao: "Configurar matriz de Probabilidade × Severidade",
      prompt: 6,
    },
    {
      id: "sugestoes",
      label: "Matriz de Sugestões",
      descricao: "Definir sugestões de ações preventivas",
      prompt: 7,
    },
  ];

  return (
    <SalviaLayout title="ERGOS - Metodologia Ativa">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-[#002F59]">ERGOS</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Gerenciar templates, matrizes e versionamento da metodologia ERGOS
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => setLocation("/motor-analise")}
              variant="outline"
              className="gap-2"
            >
              ← Voltar ao Motor de Análise
            </Button>
            <Button 
              onClick={() => setWizardOpen(true)}
              className="gap-2"
            >
              <Wand2 size={16} />
              Assistente ERGOS
            </Button>
          </div>
        </div>

        <Tabs 
          value={activeTab} 
          onValueChange={(tab) => {
            setActiveTab(tab);
            // Prefetch ao mudar de aba via keyboard
            handleTabMouseEnter(tab);
          }} 
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-5">
            {abas.map(aba => (
              <TabsTrigger 
                key={aba.id} 
                value={aba.id} 
                className="text-xs"
                onMouseEnter={() => handleTabMouseEnter(aba.id)}
              >
                {aba.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {abas.map(aba => {
            if (aba.id === "template") {
              return (
                <TabsContent key={aba.id} value={aba.id} className="space-y-4">
                  <TemplateTabContent setActiveTab={setActiveTab} />
                </TabsContent>
              );
            }

            if (aba.id === "qualificacao") {
              return (
                <TabsContent key={aba.id} value={aba.id} className="space-y-4">
                  <QualificationMatrixTab />
                </TabsContent>
              );
            }

            if (aba.id === "fontes") {
              return (
                <TabsContent key={aba.id} value={aba.id} className="space-y-4">
                  <SourcesMatrixTab />
                </TabsContent>
              );
            }

            if (aba.id === "classificacao") {
              return (
                <TabsContent key={aba.id} value={aba.id} className="space-y-4">
                  <ClassificationMatrixTab />
                </TabsContent>
              );
            }

            if (aba.id === "sugestoes") {
              return (
                <TabsContent key={aba.id} value={aba.id} className="space-y-4">
                  <SuggestionsMatrixTab />
                </TabsContent>
              );
            }

            return (
              <TabsContent key={aba.id} value={aba.id} className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>{aba.label}</CardTitle>
                    <CardDescription>{aba.descricao}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Alert>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        Esta etapa depende da publicação da etapa anterior.
                      </AlertDescription>
                    </Alert>

                    <div className="flex flex-col items-center justify-center py-12 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                      <AlertCircle size={48} className="text-gray-400 mb-4" />
                      <h3 className="text-lg font-semibold text-gray-600 mb-2">
                        Em Construção
                      </h3>
                      <p className="text-sm text-gray-500 text-center max-w-md">
                        Esta funcionalidade será implementada no <strong>Prompt {aba.prompt}</strong>.
                      </p>
                      <p className="text-xs text-gray-400 mt-4">
                        Configuração ainda não implementada. Aguarde os próximos prompts.
                      </p>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm font-medium text-blue-900">
                        Status: <span className="text-blue-600">Não configurado</span>
                      </p>
                      <p className="text-xs text-blue-700 mt-1">
                        Aguardando implementação da funcionalidade específica desta aba.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            );
          })}
        </Tabs>
      </div>

      {wizardOpen && (
        <ERGOSWizard 
          isOpen={wizardOpen}
          onClose={() => setWizardOpen(false)}
          onTabChange={setActiveTab}
          abas={abas}
        />
      )}
    </SalviaLayout>
  );
}
